import { duplicarCamada, reordenarCamada, type Camada, type DestinoNaOrdem } from "@foto/shared";
import { cabeOutraCamada } from "./camadas-da-celula.js";

/**
 * O estado do editor de camadas, com o histórico de desfazer e refazer, sem React.
 *
 * Separado do componente porque as regras são sutis — o que é "uma edição", quando uma sequência vira uma
 * entrada só, o que sobra da seleção depois de desfazer — e regra se testa sem navegador.
 *
 * **Um gesto = uma entrada.** Arrastar uma figurinha dispara dezenas de edições por segundo, e desfazer cada
 * pixel seria inútil. Quem edita em sequência dá a mesma `chave` (um gesto, uma digitação, um controle
 * deslizante), e as edições seguintes **atualizam** a entrada que já está na pilha em vez de empilhar outra.
 * Qualquer ação que não seja uma edição com a mesma chave (selecionar, desfazer, uma edição de outra chave)
 * fecha o agrupamento.
 */

/** Quantas edições o desfazer lembra. */
export const MAX_ENTRADAS_DO_HISTORICO = 30;

export interface EstadoDaEdicao {
  /** As camadas, de baixo para cima: o fim é a frente. */
  camadas: readonly Camada[];
  selecionada: string | null;
  /** As camadas de antes de cada edição, da mais antiga à mais recente. */
  passado: readonly (readonly Camada[])[];
  /** O que o desfazer tirou, da que se refaz primeiro para a última. */
  futuro: readonly (readonly Camada[])[];
  /** A chave da última edição empilhada; a edição seguinte com a mesma chave a reaproveita. */
  chave: string | null;
}

export type AcaoDaEdicao =
  | { tipo: "selecionar"; id: string | null }
  /** Troca uma camada por outra com o mesmo `id` (todo gesto e todo controle do inspetor passam por aqui). */
  | { tipo: "alterar"; camada: Camada; chave?: string }
  | { tipo: "adicionar"; camada: Camada }
  | { tipo: "apagar"; id: string }
  | { tipo: "duplicar"; id: string }
  | { tipo: "ordem"; id: string; para: DestinoNaOrdem }
  /** Termina um agrupamento (soltou o controle, tirou o dedo, saiu do campo). */
  | { tipo: "fechar-agrupamento" }
  | { tipo: "desfazer" }
  | { tipo: "refazer" };

export function estadoInicial(camadas: readonly Camada[]): EstadoDaEdicao {
  return { camadas, selecionada: null, passado: [], futuro: [], chave: null };
}

export const podeDesfazer = (e: EstadoDaEdicao): boolean => e.passado.length > 0;
export const podeRefazer = (e: EstadoDaEdicao): boolean => e.futuro.length > 0;

/** As mesmas camadas, com os mesmos valores? (As referências mudam a cada gesto; o conteúdo, nem sempre.) */
export function camadasIguais(a: readonly Camada[], b: readonly Camada[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((c, i) => c === b[i] || JSON.stringify(c) === JSON.stringify(b[i]));
}

const recortar = <T>(lista: readonly T[], max: number): T[] =>
  lista.length > max ? lista.slice(lista.length - max) : [...lista];

/**
 * Aplica uma edição às camadas. Se o resultado é igual ao que havia, nada muda (um gesto que não saiu do
 * lugar não vira entrada de histórico). Edição nova apaga o que se podia refazer.
 */
function editar(
  estado: EstadoDaEdicao,
  camadas: readonly Camada[],
  opcoes: { chave?: string | undefined; selecionada?: string | null } = {},
): EstadoDaEdicao {
  const selecionada =
    opcoes.selecionada !== undefined
      ? opcoes.selecionada
      : camadas.some((c) => c.id === estado.selecionada)
        ? estado.selecionada
        : null;

  if (camadasIguais(estado.camadas, camadas)) {
    return selecionada === estado.selecionada ? estado : { ...estado, selecionada, chave: null };
  }

  const chave = opcoes.chave ?? null;
  // Mesma chave da edição anterior: é a mesma entrada, só atualizada.
  if (chave !== null && chave === estado.chave) {
    return { ...estado, camadas, selecionada };
  }
  return {
    camadas,
    selecionada,
    passado: recortar([...estado.passado, estado.camadas], MAX_ENTRADAS_DO_HISTORICO),
    futuro: [],
    chave,
  };
}

export function reducerDaEdicao(estado: EstadoDaEdicao, acao: AcaoDaEdicao): EstadoDaEdicao {
  switch (acao.tipo) {
    case "selecionar": {
      const id = acao.id !== null && estado.camadas.some((c) => c.id === acao.id) ? acao.id : null;
      return id === estado.selecionada && estado.chave === null
        ? estado
        : { ...estado, selecionada: id, chave: null };
    }

    case "alterar": {
      if (!estado.camadas.some((c) => c.id === acao.camada.id)) return estado;
      return editar(
        estado,
        estado.camadas.map((c) => (c.id === acao.camada.id ? acao.camada : c)),
        { chave: acao.chave },
      );
    }

    case "adicionar": {
      if (!cabeOutraCamada(estado.camadas)) return estado;
      if (estado.camadas.some((c) => c.id === acao.camada.id)) return estado;
      return editar(estado, [...estado.camadas, acao.camada], { selecionada: acao.camada.id });
    }

    case "apagar": {
      if (!estado.camadas.some((c) => c.id === acao.id)) return estado;
      return editar(
        estado,
        estado.camadas.filter((c) => c.id !== acao.id),
        { selecionada: estado.selecionada === acao.id ? null : estado.selecionada },
      );
    }

    case "duplicar": {
      const copia = duplicarCamada(estado.camadas, acao.id);
      if (!copia) return estado;
      return editar(estado, copia.camadas, { selecionada: copia.novaId });
    }

    case "ordem":
      return editar(estado, reordenarCamada(estado.camadas, acao.id, acao.para));

    case "fechar-agrupamento":
      return estado.chave === null ? estado : { ...estado, chave: null };

    case "desfazer": {
      const anterior = estado.passado[estado.passado.length - 1];
      if (!anterior) return estado;
      return {
        camadas: anterior,
        selecionada: anterior.some((c) => c.id === estado.selecionada) ? estado.selecionada : null,
        passado: estado.passado.slice(0, -1),
        futuro: recortar([estado.camadas, ...estado.futuro], MAX_ENTRADAS_DO_HISTORICO),
        chave: null,
      };
    }

    case "refazer": {
      const proxima = estado.futuro[0];
      if (!proxima) return estado;
      return {
        camadas: proxima,
        selecionada: proxima.some((c) => c.id === estado.selecionada) ? estado.selecionada : null,
        passado: recortar([...estado.passado, estado.camadas], MAX_ENTRADAS_DO_HISTORICO),
        futuro: estado.futuro.slice(1),
        chave: null,
      };
    }
  }
}
